/**
 * Employee Portal Messages Page
 *
 * Messaging interface for employees to communicate with:
 * - Benefits team
 * - Care coordinators
 * - HR/Employer
 */

'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageSquare,
  Send,
  Paperclip,
  Search,
  Inbox,
  Heart,
  Building2,
  User,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Mail,
  MailOpen,
  RefreshCw,
  ArrowLeft,
  Flag,
} from 'lucide-react';
import { getMemberData, getMemberToken, getCommunications, type Communication } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';
import EmployeePortalLayout from '../layout';
import ComposeMessageModal from '@/components/member/ComposeMessageModal';

type TabType = 'inbox' | 'sent' | 'benefits' | 'care_team' | 'employer';

// Conversation groups messages by channel or communication thread
interface Conversation {
  id: string;  // channel_id for workspace, 'comm-{id}' for member_communications
  type: 'workspace' | 'member_communications';
  channel_id?: number;
  channel_name?: string;
  subject?: string;
  messages: Communication[];
  lastMessage: Communication;
  unreadCount: number;
  participantNames: string[];
}

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  filter: (msg: Communication) => boolean;
}

const TABS: TabConfig[] = [
  {
    id: 'inbox',
    label: 'Inbox',
    icon: <Inbox className="w-4 h-4" />,
    filter: (msg) => msg.direction !== 'outbound',
  },
  {
    id: 'sent',
    label: 'Sent',
    icon: <Send className="w-4 h-4" />,
    filter: (msg) => msg.direction === 'outbound',
  },
  {
    id: 'benefits',
    label: 'Benefits',
    icon: <Heart className="w-4 h-4" />,
    filter: (msg) => msg.communication_type === 'benefits' || msg.program_name?.toLowerCase().includes('benefit'),
  },
  {
    id: 'care_team',
    label: 'Care Team',
    icon: <User className="w-4 h-4" />,
    filter: (msg) => msg.communication_type === 'care_team' || msg.channel_name?.toLowerCase().includes('care'),
  },
  {
    id: 'employer',
    label: 'HR',
    icon: <Building2 className="w-4 h-4" />,
    filter: (msg) => msg.communication_type === 'employer' || msg.channel_name?.toLowerCase().includes('hr'),
  },
];

// Helper function to group messages into conversations
function groupMessagesIntoConversations(messages: Communication[]): Conversation[] {
  const conversationMap = new Map<string, Conversation>();

  for (const msg of messages) {
    let conversationId: string;
    let type: 'workspace' | 'member_communications';

    if (msg.source === 'workspace' && msg.channel_id) {
      conversationId = `channel-${msg.channel_id}`;
      type = 'workspace';
    } else {
      // For member_communications, each message is its own conversation unless we have threading info
      conversationId = `comm-${msg.id}`;
      type = 'member_communications';
    }

    if (conversationMap.has(conversationId)) {
      const conv = conversationMap.get(conversationId)!;
      conv.messages.push(msg);
      // Update last message if this one is newer
      if (new Date(msg.created_at || msg.sent_at || '') > new Date(conv.lastMessage.created_at || conv.lastMessage.sent_at || '')) {
        conv.lastMessage = msg;
      }
      // Track unique participant names
      const senderName = msg.sender_name || msg.sent_by_name;
      if (senderName && !conv.participantNames.includes(senderName)) {
        conv.participantNames.push(senderName);
      }
      // Count unread messages
      if (!msg.is_read && !msg.read_at && msg.direction !== 'outbound') {
        conv.unreadCount++;
      }
    } else {
      const senderName = msg.sender_name || msg.sent_by_name;
      conversationMap.set(conversationId, {
        id: conversationId,
        type,
        channel_id: msg.channel_id,
        channel_name: msg.channel_name,
        subject: msg.subject,
        messages: [msg],
        lastMessage: msg,
        unreadCount: (!msg.is_read && !msg.read_at && msg.direction !== 'outbound') ? 1 : 0,
        participantNames: senderName ? [senderName] : [],
      });
    }
  }

  // Sort messages within each conversation by date (oldest first for display)
  for (const conv of conversationMap.values()) {
    conv.messages.sort((a, b) =>
      new Date(a.created_at || a.sent_at || '').getTime() - new Date(b.created_at || b.sent_at || '').getTime()
    );
  }

  // Return conversations sorted by last message date (newest first)
  return Array.from(conversationMap.values()).sort((a, b) =>
    new Date(b.lastMessage.created_at || b.lastMessage.sent_at || '').getTime() -
    new Date(a.lastMessage.created_at || a.lastMessage.sent_at || '').getTime()
  );
}

export default function MessagesPage() {
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('inbox');
  useSubtabNavigation(
    (tab) => setActiveTab(tab as TabType),
    ['inbox', 'sent', 'benefits', 'care_team', 'employer'] as const,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  const member = getMemberData();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchMessages = useCallback(async (showRefreshing = false) => {
    if (!member?.id) {
      setLoading(false);
      return;
    }

    if (showRefreshing) setRefreshing(true);

    try {
      const data = await getCommunications();
      if (!mountedRef.current) return;
      setCommunications(data || []);
      // Group messages into conversations for threaded view
      setConversations(groupMessagesIntoConversations(data || []));
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('Error fetching communications:', error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [member?.id]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Filter conversations based on active tab and search
  const filteredConversations = conversations.filter(conv => {
    // Apply tab filter - check if any message in conversation matches
    const tabConfig = TABS.find(t => t.id === activeTab);
    if (tabConfig) {
      const hasMatchingMessage = conv.messages.some(tabConfig.filter);
      if (!hasMatchingMessage) return false;
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return conv.messages.some(msg =>
        msg.subject?.toLowerCase().includes(search) ||
        msg.message_body?.toLowerCase().includes(search) ||
        msg.sender_name?.toLowerCase().includes(search) ||
        msg.sent_by_name?.toLowerCase().includes(search) ||
        conv.channel_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Calculate counts
  const unreadCount = communications.filter(c => !c.is_read && !c.read_at && c.direction !== 'outbound').length;
  const urgentCount = communications.filter(c => c.priority === 'high' || c.priority === 'urgent').length;

  const getTabCount = (tabId: TabType): number => {
    const tabConfig = TABS.find(t => t.id === tabId);
    if (!tabConfig) return 0;

    // Count conversations that have matching messages
    const matchingConversations = conversations.filter(conv =>
      conv.messages.some(tabConfig.filter)
    );

    if (tabId === 'inbox') {
      // Count unread conversations
      return matchingConversations.filter(c => c.unreadCount > 0).length;
    }
    return matchingConversations.length;
  };

  const handleMarkAsRead = async (message: Communication) => {
    if (message.is_read || message.read_at) return;

    try {
      const token = getMemberToken();
      if (!token) return;

      let url = getWeaveinApiUrl(`/api/member-portal/communications/${message.id}/read`);
      if (message.source === 'workspace' && message.channel_id) {
        const params = new URLSearchParams({
          source: 'workspace',
          channel_id: message.channel_id.toString()
        });
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Update both communications and conversations state
        setCommunications(prev =>
          prev.map(c => c.id === message.id && c.source === message.source
            ? { ...c, is_read: true, read_at: new Date().toISOString(), status: 'read' }
            : c)
        );

        // Also update conversations state to reflect the change in UI
        setConversations(prev => prev.map(conv => ({
          ...conv,
          messages: conv.messages.map(m =>
            m.id === message.id && m.source === message.source
              ? { ...m, is_read: true, read_at: new Date().toISOString(), status: 'read' }
              : m
          ),
          unreadCount: conv.messages.filter(m =>
            !(m.id === message.id && m.source === message.source) &&
            !m.is_read && !m.read_at && m.direction !== 'outbound'
          ).length
        })));
      }
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowMobileDetail(true);
    // Mark all unread messages in the conversation as read
    conversation.messages.forEach(msg => {
      if (!msg.is_read && !msg.read_at && msg.direction !== 'outbound') {
        handleMarkAsRead(msg);
      }
    });
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;

    setSendingReply(true);
    try {
      const token = getMemberToken();
      if (!token) return;

      // Use different endpoints based on conversation type
      let url: string;
      let body: object;

      if (selectedConversation.type === 'workspace' && selectedConversation.channel_id) {
        // For workspace messages, use the workspace send endpoint
        url = getWeaveinApiUrl(`/api/member-portal/communications/workspace/${selectedConversation.channel_id}/send`);
        body = {
          message: replyText  // Backend expects 'message' field
        };
      } else {
        // For regular communications, use the respond endpoint with the last message
        const lastMessage = selectedConversation.lastMessage;
        url = getWeaveinApiUrl(`/api/member-portal/communications/${lastMessage.id}/respond`);
        body = {
          communication_id: lastMessage.id,
          response_message: replyText
        };
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setReplyText('');
        // Refresh messages to show the reply
        fetchMessages(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error sending reply:', response.status, errorData);
      }
    } catch (err) {
      console.error('Error sending reply:', err);
    } finally {
      setSendingReply(false);
    }
  };

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'care_team': return <Heart className="w-5 h-5 text-pink-500" />;
      case 'employer': return <Building2 className="w-5 h-5 text-blue-500" />;
      case 'benefits': return <Heart className="w-5 h-5 text-green-500" />;
      default: return <User className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <EmployeePortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </EmployeePortalLayout>
    );
  }

  return (
    <EmployeePortalLayout>
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              {urgentCount > 0 && (
                <span className="ml-2 text-red-600">
                  <Flag className="w-4 h-4 inline mr-1" />
                  {urgentCount} urgent
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchMessages(true)}
              disabled={refreshing}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh messages"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setComposeOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              New Message
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto" aria-label="Tabs">
              {TABS.map((tab) => {
                const count = getTabCount(tab.id);
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                      ${isActive
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    {tab.icon}
                    {tab.label}
                    {count > 0 && tab.id === 'inbox' && (
                      <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Conversations List and Detail View */}
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
            {/* Conversations List */}
            <div className={`lg:col-span-1 max-h-[600px] overflow-y-auto ${showMobileDetail ? 'hidden lg:block' : ''}`}>
              {filteredConversations.length > 0 ? (
                filteredConversations.map((conversation) => {
                  const hasUnread = conversation.unreadCount > 0;
                  const isSelected = selectedConversation?.id === conversation.id;
                  const lastMsg = conversation.lastMessage;
                  const hasUrgent = conversation.messages.some(m => m.priority === 'high' || m.priority === 'urgent');
                  return (
                    <div
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation)}
                      className={`
                        p-4 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors
                        ${isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''}
                        ${hasUnread ? 'bg-blue-50' : ''}
                      `}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          {hasUnread ? (
                            <Mail className="w-5 h-5 text-indigo-600" />
                          ) : (
                            <MailOpen className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm truncate ${hasUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                              {conversation.participantNames.join(', ') || 'System'}
                            </p>
                            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                              {formatDate(lastMsg.sent_at || lastMsg.created_at)}
                            </span>
                          </div>
                          <p className={`text-sm truncate mt-0.5 ${hasUnread ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                            {conversation.channel_name || conversation.subject || 'No subject'}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-1">
                            {lastMsg.message_body?.substring(0, 80)}...
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            {conversation.messages.length > 1 && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                                <MessageSquare className="w-3 h-3 mr-1" />
                                {conversation.messages.length} messages
                              </span>
                            )}
                            {hasUrgent && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Urgent
                              </span>
                            )}
                            {conversation.unreadCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                {conversation.unreadCount} new
                              </span>
                            )}
                          </div>
                        </div>
                        {hasUnread && (
                          <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    {searchTerm ? 'No conversations found matching your search' : 'No conversations in this folder'}
                  </p>
                </div>
              )}
            </div>

            {/* Conversation Detail - Threaded View */}
            <div className={`lg:col-span-2 min-h-[600px] ${!showMobileDetail ? 'hidden lg:block' : ''}`}>
              {selectedConversation ? (
                <div className="h-full flex flex-col">
                  {/* Mobile back button */}
                  <div className="lg:hidden p-3 border-b border-gray-200">
                    <button
                      onClick={() => {
                        setShowMobileDetail(false);
                        setSelectedConversation(null);
                      }}
                      className="flex items-center text-gray-600 hover:text-gray-900"
                    >
                      <ArrowLeft className="w-5 h-5 mr-2" />
                      Back to conversations
                    </button>
                  </div>

                  {/* Conversation Header */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getMessageIcon(selectedConversation.messages[0]?.communication_type || 'general')}
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {selectedConversation.channel_name || selectedConversation.subject || 'Conversation'}
                          </h2>
                          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
                            <span className="text-sm text-gray-600">
                              {selectedConversation.participantNames.join(', ') || 'System'}
                            </span>
                            <span className="text-gray-300">|</span>
                            <span className="text-sm text-gray-500">
                              {selectedConversation.messages.length} message{selectedConversation.messages.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Threaded Messages */}
                  <div className="flex-1 p-4 overflow-y-auto space-y-4">
                    {selectedConversation.messages.map((msg, idx) => {
                      const isOutbound = msg.direction === 'outbound';
                      return (
                        <div
                          key={`${msg.source}-${msg.id}-${idx}`}
                          className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-4 ${
                              isOutbound
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium ${isOutbound ? 'text-indigo-200' : 'text-gray-500'}`}>
                                {isOutbound ? 'You' : msg.sender_name || msg.sent_by_name || 'System'}
                              </span>
                              <span className={`text-xs ${isOutbound ? 'text-indigo-200' : 'text-gray-400'}`}>
                                {new Date(msg.sent_at || msg.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">
                              {msg.message_body}
                            </p>
                            {(msg.priority === 'high' || msg.priority === 'urgent') && (
                              <div className={`flex items-center gap-1 mt-2 text-xs ${isOutbound ? 'text-red-200' : 'text-red-600'}`}>
                                <Flag className="w-3 h-3" />
                                {msg.priority === 'urgent' ? 'Urgent' : 'High Priority'}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply Section */}
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-start space-x-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        rows={2}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      />
                      <div className="flex flex-col gap-2">
                        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleSendReply}
                          disabled={!replyText.trim() || sendingReply}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {sendingReply ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Send
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500">Select a conversation to read</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compose Modal */}
      <ComposeMessageModal
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSuccess={() => {
          setComposeOpen(false);
          fetchMessages(true);
        }}
      />
    </EmployeePortalLayout>
  );
}
